import {
  WSActions,
  WSControl,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSForm,
  WSList,
  WSLoader,
  WSModalOld,
  WSSelect,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayoutDestination,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { WSQueryCache } from "@ws-react-query";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import { addNotification } from "../../components/Notification/Notification";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { QUERY_KEY_INVOICES_ROWS } from "../../modules/Invoicing/queries/useQueryInvoicesRows";
import {
  getAccountLabelProps,
  getCheckbookCardLabelProps,
  getInternalAccountLabelProps,
  getPaymentCardLabelProps
} from "../../modules/Settings/components/ManageMethods/utils";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD } from "../../modules/Settings/routes/RoutePaymentMethods";
import { useQueryInternalAccounts } from "../../query/banking/queries/useQueryInternalAccounts";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { useQueryCustomerEntity } from "../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryPaymentsCardsDebit } from "../../query/paymentCards/queries/useQueryPaymentsCardsDebit";
import {
  QUERY_INVOICE,
  QUERY_INVOICES,
  QUERY_PAYOUT_SETTINGS
} from "../../query/payments/keys";
import {
  useInvoiceQuery,
  usePayoutSettings,
  usePayoutSettingsDebitCards
} from "../../query/payments/queries";
import { useAccounts, useMemberProfile } from "../../query/users/queries";
import { paymentsService } from "../../services/payments";
import { ErrorContextKey } from "../../services/platform";
import { selectorAccountIPReady } from "../../shared/selectors/selectorAccountIPReady";
import { selectorActiveBankingAccount } from "../../shared/selectors/selectorActiveBankingAccount";
import { selectorInstantPayoutDefaultFee } from "../../shared/selectors/selectorInstantPayoutDefaultFee";
import { selectorPaymentCardPayoutReady } from "../../shared/selectors/selectorPaymentCardPayoutReady";
import { selectorUseInternationalPayouts } from "../../shared/selectors/selectorUseInternationalPayouts";
import { instantPayoutService } from "../../shared/services/instantPayout";
import { track } from "../../utils/analytics";
import { WSServiceError } from "../../utils/serviceHelper";

function destinationToStringValue(destination?: IPayoutDestination) {
  switch (destination?.destinationType) {
    case PayoutDestinationType.Account:
      return `account ${destination?.destinationId}`;

    case PayoutDestinationType.Card:
      return `checkbook-card ${destination?.destinationId}`;

    case PayoutDestinationType.PaymentCard:
      return `payment-card ${destination?.destinationId}`;

    default:
      return null;
  }
}

type FormData = {
  payoutMethod: null | string;
  enableInstantDeposits: boolean;
};

export const InstantPayoutsModal: React.FC<
  RouteComponentProps<{
    invoiceId: string;
  }>
> = ({ match, history, location }) => {
  const onBack = () => {
    history.goBack();
  };
  const userId = useUserId();

  const invoiceId = match.params.invoiceId;
  const queryInvoice = useInvoiceQuery(invoiceId);
  const queryPayoutSettings = usePayoutSettings(userId);
  const queryAccounts = useAccounts();
  const queryPaymentCards = useQueryPaymentsCardsDebit();
  const queryDebitCards = usePayoutSettingsDebitCards(userId);
  const queryMember = useMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const queryInternalAccounts = useQueryInternalAccounts();

  const isInternational =
    queryMember.data && queryCustomerEntity.data
      ? selectorUseInternationalPayouts(
          queryMember.data,
          queryCustomerEntity.data
        )
      : false;

  const payoutMethodOptions = useMemo(() => {
    const options: WSSelectOption[] = [];

    const accounts = (queryAccounts.data || []).filter(account =>
      selectorAccountIPReady(account, isInternational)
    );
    const paymentCards = (queryPaymentCards.data || []).filter(
      selectorPaymentCardPayoutReady
    );
    const debitCards = queryDebitCards.data || [];

    const internalAccounts = (queryInternalAccounts.data || []).filter(
      selectorActiveBankingAccount
    );

    internalAccounts.forEach(internalAccount => {
      const label = getInternalAccountLabelProps(internalAccount);
      options.push({
        avatar: label.avatar,
        label: [label.text, label.secondaryText].filter(Boolean).join(" "),
        value: "internalAccount " + internalAccount.internalAccountId
      });
    });

    accounts.forEach(account => {
      const label = getAccountLabelProps(account);
      options.push({
        avatar: label.avatar,
        label: [label.text, label.secondaryText].filter(Boolean).join(" "),
        value: "account " + account.accountId
      });
    });

    paymentCards.forEach(paymentCard => {
      const label = getPaymentCardLabelProps(paymentCard);

      options.push({
        avatar: label.avatar,
        label: [label.text, label.secondaryText].filter(Boolean).join(" "),
        value: "payment-card " + paymentCard.paymentCardId
      });
    });

    debitCards.forEach(debitCard => {
      const label = getCheckbookCardLabelProps(debitCard);

      options.push({
        avatar: label.avatar,
        label: ["Wingspan Wallet", label.secondaryText]
          .filter(Boolean)
          .join(" "),
        value: "checkbook-card " + debitCard.cardId
      });
    });

    return options;
  }, [
    queryAccounts.data,
    queryPaymentCards.data,
    queryDebitCards.data,
    queryInternalAccounts.data,
    isInternational
  ]);

  const [submit, submitMeta] = useWSMutation<void, WSServiceError, FormData>(
    async data => {
      if (!data.payoutMethod) {
        throw new Error("No payout method set");
      }

      track("Invoice Instant Deposit Requested", {
        invoiceId: invoiceId
      });

      const [payoutMethodType, destinationId] = data.payoutMethod.split(" ");

      let instantDestination:
        | {
            destinationType: PayoutDestinationType;
            destinationId: string;
          }
        | null
        | undefined;

      switch (payoutMethodType) {
        case "account":
          instantDestination = {
            destinationType: PayoutDestinationType.Account,
            destinationId
          };
          break;
        case "payment-card":
          instantDestination = {
            destinationType: PayoutDestinationType.PaymentCard,
            destinationId
          };
          break;
        case "checkbook-card":
          instantDestination = {
            destinationType: PayoutDestinationType.Card,
            destinationId
          };
          break;

        case "internal-account":
          instantDestination = null;
          break;
      }

      if (instantDestination === undefined) {
        throw new Error("Invalid payout method");
      }

      await paymentsService.payoutSettings.update(userId, {
        payoutPreferences: data.enableInstantDeposits
          ? PayoutPreferences.Instant
          : undefined,
        instant: instantDestination
      });

      await paymentsService.invoice.update(invoiceId, {
        member: {
          payoutPreferences: PayoutPreferences.Instant
        }
      });
    },
    {
      onSuccess: () => {
        history.goBack();

        addNotification({
          text: "Instant payout successfully requested"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(QUERY_KEY_INVOICES_ROWS);
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(QUERY_PAYOUT_SETTINGS);
      }
    }
  );

  const invoice = queryInvoice.data;
  const payoutSettings = queryPayoutSettings.data;

  const invoiceIsDeposited = !!invoice?.events.depositedAt;

  const invoiceAmount = Math.abs(invoice?.amountDetails?.memberGross || 0);
  const instantPayoutFee =
    instantPayoutService.calculateInvoiceInstantPayoutFee(
      Math.abs(invoiceAmount),
      payoutSettings
    );
  const instantPayoutAmount = invoiceAmount - instantPayoutFee;

  if (
    !queryInvoice.data ||
    !queryPayoutSettings.data ||
    !queryAccounts.data ||
    !queryPaymentCards.data ||
    !queryDebitCards.data
  ) {
    return (
      <WSModalOld size="S" onClose={onBack}>
        <WSLoader.Spinner />
      </WSModalOld>
    );
  }

  if (invoiceIsDeposited) {
    return (
      <WSModalOld
        size="S"
        title={`Invoice #${queryInvoice.data?.invoiceNumber}`}
        onClose={onBack}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            This invoice has already been deposited
          </WSText.ParagraphSm>

          <WSActions
            orientation="vertical"
            buttons={[
              {
                kind: "Tertiary",
                label: "Close",
                onClick: onBack
              }
            ]}
          />
        </WSList>
      </WSModalOld>
    );
  }

  if (!payoutMethodOptions.length) {
    return (
      <WSModalOld
        size="S"
        title={`Instant payout for invoice #${
          queryInvoice.data?.invoiceNumber || "###"
        }`}
        onClose={onBack}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            No instant payout method added
          </WSText.ParagraphSm>

          <WSActions
            orientation="vertical"
            buttons={[
              {
                kind: "Tertiary",
                label: "Close",
                onClick: onBack
              },
              {
                kind: "Secondary",
                label: "Add payout method",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }
              }
            ]}
          />
        </WSList>
      </WSModalOld>
    );
  }

  return (
    <WSModalOld
      size="S"
      title={`Instant payout for invoice #${
        queryInvoice.data?.invoiceNumber || "###"
      }`}
      onClose={onBack}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          Instant payout requests are generally processed within 90 minutes.
        </WSText.ParagraphSm>
        <WSElement>
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Invoice number
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>{invoice?.invoiceNumber}</WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Invoice amount
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(invoiceAmount, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Instant payout fee (
              {selectorInstantPayoutDefaultFee(payoutSettings)}%)
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(instantPayoutFee, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Instant payout amount
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(instantPayoutAmount, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
        </WSElement>

        <WSForm<FormData>
          defaultValues={{
            payoutMethod: destinationToStringValue(
              queryPayoutSettings.data?.payoutDestinations.find(
                d => d.payoutMethod === PayoutPreferences.Instant
              )
            ),
            enableInstantDeposits: false
          }}
          onSubmit={submit}
          validationSchema={Yup.object({
            payoutMethod: Yup.string().nullable().required("Required"),
            enableInstantDeposits: Yup.boolean().nullable()
          })}
        >
          <WSList gap="XL">
            <WSList gap="M">
              <WSForm.Field
                name="payoutMethod"
                component={WSSelect}
                label="Instant payout account"
                componentProps={{
                  options: payoutMethodOptions
                }}
              />

              <WSForm.Field
                name="enableInstantDeposits"
                component={WSControl}
                componentProps={{
                  type: "checkbox",
                  size: "S",
                  label:
                    "Make instant payouts my default for all future payments"
                }}
              />
            </WSList>

            <WSErrorMessage
              contextKey={ErrorContextKey.Other}
              error={submitMeta.error}
            />

            <WSActions
              orientation="vertical"
              buttons={[
                {
                  label: "Confirm instant payout request",
                  loading: submitMeta.isLoading
                },
                {
                  label: "Cancel",
                  kind: "Tertiary",
                  type: "button",
                  onClick: onBack
                }
              ]}
            />
          </WSList>
        </WSForm>
      </WSList>
    </WSModalOld>
  );
};
